import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Snowflake } from "lucide-react";
import { api } from "../lib/api";

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/products");
        if (!cancelled) setProducts(r.data);
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      id="lattes"
      data-testid="products-section"
      className="relative py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.18em] text-[#8D99AE] mb-4">Our Lattes</div>
          <h2
            data-testid="products-heading"
            className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#2C1E16] leading-tight"
          >
            Delicious flavors. All built around real espresso.
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#5C4E46] max-w-xl">
            Small batches, simple ingredients, made to be served cold from the
            cooler — wherever Memphis is gathering.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-8">
          {loading && [0, 1].map((i) => (
            <div
              key={i}
              data-testid={`product-skeleton-${i}`}
              className="rounded-3xl border border-[#E8E3DD] bg-white animate-pulse h-[34rem]"
            />
          ))}

          {!loading && products.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`product-card-${p.slug}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: "easeOut" }}
              className="group bg-white rounded-3xl border border-[#E8E3DD] shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden hover:shadow-[0_18px_50px_rgb(0,0,0,0.07)] transition-shadow"
            >
              <div className="relative aspect-[4/5] sm:aspect-[5/6] overflow-hidden bg-[#E8E3DD]">
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#8D99AE] text-sm">
                    No image yet
                  </div>
                )}
                {p.served_cold_visible && p.served_cold_label && (
                  <span
                    data-testid={`product-pill-served-cold-${p.slug}`}
                    className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-[#FAF9F6]/90 backdrop-blur px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.14em] text-[#2C1E16] border border-[#E8E3DD]"
                  >
                    <Snowflake size={13} strokeWidth={1.5} />
                    {p.served_cold_label}
                  </span>
                )}
              </div>

              <div className="p-7 sm:p-9">
                <h3
                  data-testid={`product-name-${p.slug}`}
                  className="font-display text-2xl sm:text-3xl text-[#2C1E16]"
                >
                  {p.name}
                </h3>
                <p className="mt-3 text-[#5C4E46] leading-relaxed">{p.description}</p>
                {p.notes && p.notes.length > 0 && (
                  <ul className="mt-5 flex flex-wrap gap-2">
                    {p.notes.map((n) => (
                      <li
                        key={n}
                        className="text-[11px] uppercase tracking-[0.14em] text-[#8D99AE] bg-[#8D99AE]/10 px-3 py-1 rounded-full"
                      >
                        {n}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
